/**
 * @bos/portal3 — the dashboard (route `/app`, inside <AppShell>): the user's projects.
 *
 * M4's payoff, reframed for M6: a first-time owner is GUIDED (a welcome + the three-step
 * journey) rather than dropped into a blank list; a returning owner sees their projects
 * as calm cards in a grid. Creating a project now captures the business itself — a name,
 * an industry, and a short description (the metadata the factory will later read). Every
 * project links to its own addressable URL. Loading / empty / error states are handled.
 */
import { useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from '../routes';
import { PageHeader } from '../components/PageHeader';
import { IndustryField } from '../projects/IndustryField';
import { useProjectList, projectErrorMessage, type ProjectListRow } from '../projects/hooks';

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function ProjectCard({ project }: { project: ProjectListRow }) {
  const archived = project.status === 'archived';
  return (
    <li className={`project-card${archived ? ' project-card--archived' : ''}`}>
      <Link className="project-card__link" to={PATHS.project(project.id)}>
        <div className="project-card__head">
          <span className="project-card__name">{project.name}</span>
          <span className={`badge badge--${archived ? 'archived' : 'active'}`}>
            {archived ? 'Archived' : 'Active'}
          </span>
        </div>
        {project.industry && <span className="badge badge--industry">{project.industry}</span>}
        {project.description && <p className="project-card__desc">{project.description}</p>}
        <div className="project-card__foot">
          <span className="project-card__meta">Updated {formatDate(project.updatedAt)}</span>
        </div>
      </Link>
      {/* A HALTED BUILD, SAID WHERE THE OWNER IS LOOKING. This card used to show no run
          state at all, so the one screen a returning owner always opens could not tell
          them the machine had stopped for them. Outside the card's own link so it goes
          straight to the door rather than the long way round.
          The guard is a real id, not `!== null`: a server older than this field sends no
          key at all, and "a build is waiting" pointing at /runs/undefined would be both a
          lie and a dead end. No id ⇒ nothing is claimed. */}
      {typeof project.waitingRunId === 'string' && project.waitingRunId !== '' && (
        <Link className="waitchip project-card__waiting" to={PATHS.run(project.waitingRunId)}>
          <span aria-hidden="true">⏸</span> A build is waiting for you
        </Link>
      )}
      <LatestBuildLine project={project} />
    </li>
  );
}

/** THE SHORTCUT TO THE BUILD. Reaching a build from this card used to take four clicks
 *  through two "Open" links; the owner's actual destination gets its own line. Same guard
 *  discipline as the waitchip (a real id string, never `!== null` — an older server sends
 *  no key at all), and when the newest build is the one the waitchip already names, one
 *  line is enough: nothing is said twice. */
function LatestBuildLine({ project }: { project: ProjectListRow }) {
  const latest = project.latestRun;
  if (latest == null || typeof latest.id !== 'string' || latest.id === '') return null;
  if (latest.id === project.waitingRunId) return null;
  const words =
    latest.status === 'running'
      ? 'A build is running — watch it'
      : latest.status === 'completed'
        ? 'Last build: finished — open it'
        : 'Last build: stopped early — open it';
  return (
    <Link className="project-card__lastbuild" to={PATHS.run(latest.id)}>
      {words} <span aria-hidden="true">→</span>
    </Link>
  );
}

function Onboarding({ onStart }: { onStart: () => void }) {
  return (
    <div className="onboard">
      <div className="onboard__top">
        <p className="eyebrow">Getting started</p>
        <h2 className="onboard__title">Welcome to Sorvel</h2>
        <p className="onboard__lede">
          Sorvel is where your business becomes a working app. Three steps get you there —
          start with the first, it only takes a minute.
        </p>
      </div>
      <ol className="steps">
        <li className="step step--now">
          <span className="step__num">1</span>
          <div className="step__body">
            <span className="step__name">Create a project for your business</span>
            <p className="step__desc">
              Give your business a home: its name, its trade, and a line about what it does.
            </p>
          </div>
        </li>
        <li className="step">
          <span className="step__num">2</span>
          <div className="step__body">
            <span className="step__name">Add your documents</span>
            <p className="step__desc">
              Upload the paperwork behind it — menus, price lists, policies. They become the
              raw material.
            </p>
          </div>
        </li>
        <li className="step">
          <span className="step__num">3</span>
          <div className="step__body">
            <span className="step__name">
              Describe your app <span className="badge badge--soon">Coming soon</span>
            </span>
            <p className="step__desc">
              Tell Sorvel what you need and it builds a working app for your business.
            </p>
          </div>
        </li>
      </ol>
      <div className="onboard__actions">
        <button type="button" className="btn btn--primary" onClick={onStart}>
          Create your first project
        </button>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { projects, status, error, reload, create } = useProjectList();

  // ── new-project form ──
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // ── archived toggle ──
  const [showArchived, setShowArchived] = useState(true);
  const archivedCount = useMemo(
    () => projects.filter((p) => p.status === 'archived').length,
    [projects],
  );
  const visible = showArchived ? projects : projects.filter((p) => p.status === 'active');

  const isEmpty = status === 'ready' && projects.length === 0;
  const hasProjects = status === 'ready' && projects.length > 0;

  function openCreate() {
    setShowCreate(true);
    // Let the form mount, then put the cursor in the first field.
    setTimeout(() => nameRef.current?.focus(), 0);
  }

  async function onCreate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setBusy(true);
    try {
      await create({
        name,
        description: description.trim() || undefined,
        industry: industry.trim() || undefined,
      });
      setName('');
      setIndustry('');
      setDescription('');
      setShowCreate(false);
    } catch (err) {
      setFormError(projectErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page--dashboard">
      <PageHeader
        eyebrow="Workspace"
        title="Your projects"
        lede="Each project is a business you’re bringing into Sorvel — its details and the documents behind it."
        actions={
          hasProjects ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => (showCreate ? setShowCreate(false) : openCreate())}
            >
              {showCreate ? 'Close' : 'New project'}
            </button>
          ) : null
        }
      />

      {status === 'loading' && (
        <p className="page__note" aria-busy="true">
          Loading your projects…
        </p>
      )}

      {status === 'error' && (
        <div className="projects__state">
          <p className="form__error" role="alert">
            {error ?? 'We couldn’t load your projects.'}
          </p>
          <button className="btn btn--ghost" type="button" onClick={() => void reload()}>
            Try again
          </button>
        </div>
      )}

      {isEmpty && !showCreate && (
        <div className="stack">
          <Onboarding onStart={openCreate} />
        </div>
      )}

      {showCreate && (
        <div className="card">
          <h2 className="card__title">New project</h2>
          <p className="card__intro">Tell us about the business this project is for.</p>
          <form className="form" onSubmit={onCreate} aria-label="New project" style={{ marginTop: '1.1rem' }}>
            {formError && (
              <p className="form__error" role="alert">
                {formError}
              </p>
            )}
            <label className="form__field">
              <span className="form__label">Business name</span>
              <input
                ref={nameRef}
                className="form__input"
                type="text"
                name="name"
                placeholder="e.g. Corner Street Bakery"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={busy}
              />
            </label>
            <IndustryField value={industry} onChange={setIndustry} disabled={busy} />
            <label className="form__field">
              <span className="form__label">Description (optional)</span>
              <textarea
                className="form__textarea"
                name="description"
                placeholder="A sentence about what the business does."
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={busy}
              />
            </label>
            <div className="form__actions">
              <button className="btn btn--primary" type="submit" disabled={busy || name.trim() === ''}>
                {busy ? 'Creating…' : 'Create project'}
              </button>
              <button
                type="button"
                className="btn btn--quiet"
                onClick={() => setShowCreate(false)}
                disabled={busy}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {hasProjects && (
        <>
          <div className="projects-toolbar">
            <h2 className="projects-toolbar__title">
              {projects.length} project{projects.length === 1 ? '' : 's'}
            </h2>
            {archivedCount > 0 && (
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
                <span>Show archived ({archivedCount})</span>
              </label>
            )}
          </div>

          {visible.length > 0 ? (
            <ul className="project-grid">
              {visible.map((p) => (
                <ProjectCard key={p.id} project={p} />
              ))}
            </ul>
          ) : (
            <p className="page__note">
              All your projects are archived. Toggle “Show archived” to see them.
            </p>
          )}
        </>
      )}
    </section>
  );
}
